import React, { createContext, useContext } from 'react';
import type { NotificationsFilter } from '@gov-nx/api';
import {
	filterErrorStatus,
	NotificationsOrder,
	notificationsQuery,
	useAxiosInstance,
	usePoQuery,
} from '@gov-nx/api';
import { useNotificationDetail } from '@gov-nx/core/hooks';
import { useLocale } from '@gov-nx/core/service';
import { useFilter } from '@gov-nx/utils/common';
import { PageCode } from '../definitions/codes';
import type { NotificationsContexts } from './context.types';
import { categorizeNotifications } from './utils';

const UpozorneniContext = createContext<NotificationsContexts | null>(null);

interface NotificationsContextProviderProps {
	children: React.ReactNode;
}

export function UpozorneniContextProvider({
	children,
}: NotificationsContextProviderProps) {
	const { t } = useLocale(PageCode['upozorneni']);
	const { axiosInstance } = useAxiosInstance();

	const { filter, setPartialFilter } = useFilter<NotificationsFilter>({
		key: 'upozorneni',
		initialState: {
			razeni: NotificationsOrder['-datumACasVytvoreni'],
			start: 0,
			pocet: 10,
		},
	});

	const goToPage = (page: number) => {
		const newStart = (page - 1) * filter.pocet;
		setPartialFilter({ start: newStart });
	};

	const currentPage = Math.floor(filter.start / filter.pocet) + 1;

	const query = usePoQuery(
		{
			queryKey: ['notifications', filter],
			queryFn: async () => notificationsQuery(axiosInstance, filter),
		},
		{
			errorIgnoreFilter: filterErrorStatus(500),
		}
	);

	const data = query.data?.seznam ?? [];
	const numberOfNotifications = query.data?.pocet ?? 0;

	const selectedNotification = useNotificationDetail();
	const categorizedData = categorizeNotifications(data, t);

	return (
		<UpozorneniContext.Provider
			value={{
				data,
				filter,
				currentPage,
				goToPage,
				numberOfNotifications,
				controls: {
					initialError: query.isError,
					initialLoading: query.isLoading,
				},
				categorizedData,
				selectedNotification,
			}}>
			{children}
		</UpozorneniContext.Provider>
	);
}

export const useUpozorneniContextInstance = (): NotificationsContexts =>
	useContext(UpozorneniContext) as NotificationsContexts;
